import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// 自定義功能開關類型
export interface CustomToggle {
  key: string;
  enabled: boolean;
  labels: Record<string, string>;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: Record<string, string>;
  description: Record<string, string>;
  stripe_product_id: string | null;
  stripe_product_id_yearly: string | null;
  monthly_price: number | null;
  monthly_stripe_price_id: string | null;
  yearly_price: number | null;
  yearly_stripe_price_id: string | null;
  yearly_discount_percent: number;
  lifetime_price: number | null;
  lifetime_stripe_price_id: string | null;
  features: Record<string, string[]>;
  limits: {
    maxScripts: number;
    maxCharactersPerScript: number;
    hasMirrorMode: boolean;
    hasCustomFonts: boolean;
    hasExport: boolean;
    hasNoWatermark: boolean;
    hasVoiceSync: boolean;
    hasOfflineMode: boolean;
    hasPrioritySupport: boolean;
    // 雲端儲存限制
    cloudStorageMonthlyBytes?: number;
    cloudStorageTotalBytes?: number;
  };
  // 自定義功能開關
  custom_toggles: CustomToggle[];
  // 外觀配置
  color_class: string;
  is_popular: boolean;
  cta_text: Record<string, string>;
  // 狀態
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Stripe data from API
  stripe_product?: {
    id: string;
    name: string;
    active: boolean;
    metadata: Record<string, string>;
  } | null;
  stripe_product_yearly?: {
    id: string;
    name: string;
    active: boolean;
    metadata: Record<string, string>;
  } | null;
}

export interface CreatePlanData {
  name: string;
  display_name: Record<string, string>;
  description?: Record<string, string>;
  monthly_price?: number;
  yearly_price?: number;
  yearly_discount_percent?: number;
  lifetime_price?: number;
  features?: Record<string, string[]>;
  limits?: Partial<SubscriptionPlan["limits"]>;
  custom_toggles?: CustomToggle[];
  color_class?: string;
  is_popular?: boolean;
  cta_text?: Record<string, string>;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdatePlanData extends Partial<CreatePlanData> {
  id: string;
}

export function useSubscriptionPlans() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async (): Promise<SubscriptionPlan[]> => {
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "admin-subscription-plans",
        {
          body: { action: "list" },
        }
      );

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      return data.plans || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch plans";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const updatePlan = useCallback(async (planData: UpdatePlanData): Promise<SubscriptionPlan> => {
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "admin-subscription-plans",
        {
          body: { action: "update", ...planData },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      return data.plan;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update plan";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const createPlan = useCallback(async (planData: CreatePlanData): Promise<SubscriptionPlan> => {
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "admin-subscription-plans",
        {
          body: { action: "create", ...planData },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      return data.plan;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create plan";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const deactivatePlan = useCallback(async (planId: string): Promise<void> => {
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "admin-subscription-plans",
        {
          body: { action: "deactivate", id: planId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to deactivate plan";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  return {
    loading,
    error,
    fetchPlans,
    updatePlan,
    createPlan,
    deactivatePlan,
  };
}
